import * as React from 'react';
import { styled } from 'styled-components';

import { Button, ListItem, Modal, RadioGroup, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components';

import { useIntl } from '@/intl';
import { FlightCard, FlightCardMobile } from './FlightCard';

import { useFlightSelector } from '@/domain/prices/transports/hooks';
import { ArrivalTitle, DepartureTitle } from './Headers';
import { isEmpty } from 'ramda';
import { createFlightComponentInteractionEvent } from '@/domain/prices/analytics';
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';

const Section = styled(SpaceBetween)`
  padding: ${tk('spacing_md')};
  background-color: ${tk('color_semantic_bg_subdued')};
  outline: none;
  flex-basis: 100%;
  margin-bottom: ${tk('spacing_md')};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CardsWrapper = styled(RadioGroup.Root)`
  ${ListItem.Control} {
    display: none;
  }
`;

const ModalBody = styled(Modal.Body)`
  padding: ${tk('spacing_sm')};
`;

const SelectReturnButton = styled(Button)`
  flex-grow: 0;
  margin-left: auto;
  width: 50%;
`;
const STEPS = {
  selectDeparture: 'selectDeparture',
  selectReturn: 'selectReturn',
};

export function FlightSelectorMobile() {
  const {
    inboundRoutes,
    outboundRoutes,
    selectedOutboundRouteId,
    selectedInboundRouteId,
    inboundRoute,
    outboundRoute,
    setInboundRouteId,
    setOutboundRouteId,
  } = useFlightSelector();
  const intl = useIntl();

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [step, setStep] = React.useState<string | null>(null);
  const { sendEvent } = useAnalytics();

  if (isEmpty(outboundRoutes) || isEmpty(inboundRoutes)) return null;

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setStep(STEPS.selectDeparture);
    sendAnalyticsEvent('outbound');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setStep(null);
  };

  function changeStep(text: string) {
    setStep(text);
    sendAnalyticsEvent(text === STEPS.selectDeparture ? 'outbound' : 'inbound');
  }

  function sendAnalyticsEvent(type: string) {
    sendEvent(createFlightComponentInteractionEvent({ type, result: 'Change flight' }));
  }

  return (
    <>
      <Typography $variant="heading-md">
        {intl.formatMessage({ id: 'prices_transport_selector_flight-selector_title' })}
      </Typography>
      <Wrapper id="FlightSelector">
        <Section direction="y" spacing={tk('spacing_md')}>
          <DepartureTitle />
          {outboundRoute && <FlightCardMobile {...outboundRoute} isSelected />}
        </Section>
        <Section direction="y" spacing={tk('spacing_md')}>
          <ArrivalTitle />
          {inboundRoute && <FlightCardMobile {...inboundRoute} isSelected />}
        </Section>
        <Button $hierarchy="secondary" $withoutColor onClick={handleOpenModal}>
          {intl.formatMessage({
            id: 'prices_transport_selector_flight-selector-mobile_button',
          })}
        </Button>
        <Modal.Root onClose={handleCloseModal} open={isModalOpen}>
          <Modal.Content $size="md">
            <Modal.Header>
              <Modal.HeaderTitle
                text={intl.formatMessage({
                  id: 'prices_transport_selector_flight-selector_mobile_title',
                })}
              />
              <Modal.HeaderCloseButton />
            </Modal.Header>
            <ModalBody>
              <CardsWrapper value={selectedOutboundRouteId}>
                <SpaceBetween direction="y" spacing={tk('spacing_md')}>
                  {step === STEPS.selectDeparture && (
                    <>
                      <DepartureTitle />
                      {outboundRoutes.map((route, index) => (
                        <FlightCard
                          key={index}
                          {...route}
                          setSelected={setOutboundRouteId}
                          isSelected={selectedOutboundRouteId === route.id}
                        />
                      ))}
                    </>
                  )}
                </SpaceBetween>
              </CardsWrapper>
              <CardsWrapper value={selectedInboundRouteId}>
                <SpaceBetween direction="y" spacing={tk('spacing_md')}>
                  {step === STEPS.selectReturn && (
                    <>
                      <ArrivalTitle />
                      {inboundRoutes.map((route, index) => (
                        <FlightCard
                          key={index}
                          {...route}
                          setSelected={setInboundRouteId}
                          isSelected={selectedInboundRouteId === route.id}
                        />
                      ))}
                    </>
                  )}
                </SpaceBetween>
              </CardsWrapper>
            </ModalBody>
            <Modal.Footer>
              {step === STEPS.selectDeparture && (
                <SelectReturnButton onClick={() => changeStep(STEPS.selectReturn)}>
                  {intl.formatMessage({
                    id: 'prices_transport_selector_transport-selector-return-route',
                  })}
                </SelectReturnButton>
              )}
              {step === STEPS.selectReturn && (
                <>
                  <Button $hierarchy="tertiary" $withoutColor onClick={() => changeStep(STEPS.selectDeparture)}>
                    {intl.formatMessage({
                      id: 'prices_transport_selector_transport-selector-go-back',
                    })}
                  </Button>
                  <Button onClick={handleCloseModal}>
                    {' '}
                    {intl.formatMessage({
                      id: 'prices_transport_selector_transport-selector-confirm',
                    })}
                  </Button>
                </>
              )}
            </Modal.Footer>
          </Modal.Content>
        </Modal.Root>
      </Wrapper>
    </>
  );
}
